/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { inject } from "mobx-react";
import { SearchInputUI } from "components/search/SearchInputUI";
import React from "react";
import {
  getShowNumberOfSearchResultsFound,
  disableNextSearchButton,
  disablePreviousSearchButton,
  getDefaultSearchQuery,
  getSearchManager,
  getShowSearchProgessBar,
  getSearchInputClassName,
  getSearchWidgetClassName,
  getSearchInputStyle,
  getSearchWidgetStyle,
  getIndexOfCurrentSearchResult,
  getNumberOfSearchResultsInDocument,
  getSearchQuery,
  getSearchAddOnPanel
} from "selectors/searchSelectors";
import { getCurrRHPFeature } from "selectors/appSelectors";
import { getScrollbarSize } from "selectors/appUiStateSelectors";
import { ENTER, F3, G } from "dc-preview-utils/src/Constants/key-codes";
import { isControlORCommandPressed } from "dc-preview-utils/src/Utils/EventHandlingUtils";
import previousIcon from "dc-icons/viewer2021/S_ChevronLeftMedium_18_N.svg";
import nextIcon from "dc-icons/viewer2021/S_ChevronRightMedium_18_N.svg";
import previousIconLight from "dc-icons/organizer/s_chevronprevious_18_light.svg";
import nextIconLight from "dc-icons/organizer/s_chevronnext_18_light.svg";
import previousIconMobile from "dc-icons/viewer2021/Web_Mobile/s_chevronleftmedium_22_n.svg";
import nextIconMobile from "dc-icons/viewer2021/Web_Mobile/s_chevronrightmedium_22_n.svg";
import { getAppContext } from "../../selectors/appUiStateSelectors";
import { isTouchDevice } from "dc-preview-utils/src/Utils/DeviceUtils";
import { FormattedMessage } from "react-intl";
import { getShouldUseModernViewer, getShouldUseMobileModernViewer } from "selectors/appSelectors";
import { getCurrentNotificationBannerHeight } from "selectors/notificationSelectors";
import { SEARCH_INPUT_FIELD_FOCUS_BLUR_TIMEOUT } from "constants/constants";
import { trackSearchHideSuggestion, trackSearchShowSuggestion } from "analytics/events";

const mapStoreToProps = (stores, ownProps) => ({
  ...ownProps,
  onChange: (queryString, event, triggerFrom) => {
    getSearchManager(stores).onChange(queryString, event, triggerFrom);
  },
  onSubmit: queryString => {
    getSearchManager(stores).onSubmit(queryString);
  },
  onNextClicked: queryString => {
    getSearchManager(stores).onNextClicked(queryString);
  },
  onPreviousClicked: queryString => {
    getSearchManager(stores).onPreviousClicked(queryString);
  },
  showNumberOfSearchResults: getShowNumberOfSearchResultsFound(stores),
  closeClickHandler: () => {
    getSearchManager(stores).closeSearchUI();
    stores.searchStore.setShowSearchInputUI(false);
  },
  clearSearchUI: () => {
    getSearchManager(stores).clearSearch();
  },
  setSearchQueryInStore: query => {
    stores.searchStore.setSearchQuery(query);
  },
  getSearchQueryFromStore: () => getSearchQuery(stores),
  onKeydown: (event, queryString, inFocus = false) => {
    switch (event.keyCode) {
      case F3:
      case G:
        if (
          event.shiftKey === true &&
          (event.keyCode === F3 || (event.keyCode === G && isControlORCommandPressed(event)))
        ) {
          event.preventDefault();
          if (!disablePreviousSearchButton(stores)) {
            getSearchManager(stores).onPreviousClicked(queryString);
          }
        } else if (
          event.shiftKey !== true &&
          (event.keyCode === F3 || (event.keyCode === G && isControlORCommandPressed(event)))
        ) {
          event.preventDefault();
          if (!disableNextSearchButton(stores)) {
            getSearchManager(stores).onNextClicked(queryString, true);
          }
        }
        break;
      case ENTER:
        if (inFocus === true) {
          if (event.shiftKey === true && !isTouchDevice()) {
            event.preventDefault();
            if (!disablePreviousSearchButton(stores)) {
              getSearchManager(stores).onPreviousClicked(queryString);
            }
          } else {
            event.preventDefault();
            getSearchManager(stores).onNextClicked(queryString, true);
          }
        }
        break;
      default:
        break;
    }
  },
  disablePreviousSearchButton: disablePreviousSearchButton(stores),
  disableNextSearchButton: disableNextSearchButton(stores),
  defaultSearchQuery: getDefaultSearchQuery(stores) || getSearchManager(stores).getQueryString(),
  showSearchProgressBar: getShowSearchProgessBar(stores),
  nextIcon: getShouldUseMobileModernViewer(stores)
    ? nextIconMobile
    : ownProps.options.color === "light"
    ? nextIcon
    : nextIconLight,
  previousIcon: getShouldUseMobileModernViewer(stores)
    ? previousIconMobile
    : ownProps.options.color === "light"
    ? previousIcon
    : previousIconLight,
  searchInputClassName: getSearchInputClassName(stores),
  searchWidgetClassName: getSearchWidgetClassName(stores),
  searchInputStyle: {
    ...getSearchInputStyle(stores),
    marginTop: `${getCurrentNotificationBannerHeight(stores)}px`
  },
  searchWidgetStyle: getSearchWidgetStyle(stores),
  appContext: getAppContext(stores),
  totalSearchResultsCount: getNumberOfSearchResultsInDocument(stores),
  occurrencesString: getShouldUseModernViewer(stores) ? (
    <FormattedMessage
      id="find.modernviewer.occurences"
      values={{
        currentResult: getIndexOfCurrentSearchResult(stores),
        totalResults: getNumberOfSearchResultsInDocument(stores)
      }}
    />
  ) : (
    <FormattedMessage
      id="find.occurrences"
      values={{
        number: getNumberOfSearchResultsInDocument(stores)
      }}
    />
  ),
  useModernViewer: getShouldUseModernViewer(stores),
  shouldUseMobileModernViewer: getShouldUseMobileModernViewer(stores),
  setShouldApplyFocusOnThumbnail: value => stores.thumbnailStore.setShouldApplyFocus(value),
  isRHPPanelOpened: !!getCurrRHPFeature(stores),
  docViewRects: stores.documentStore.docViewRects,
  scrollbarSize: getScrollbarSize(stores),
  searchAddOnPanel: getSearchAddOnPanel(stores)
});

export const SearchInputUIContainer = inject(mapStoreToProps)(SearchInputUI);

SearchInputUIContainer.defaultProps = {
  options: {
    color: "light"
  }
};
